import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import HelpModeDrawer from './HelpModeDrawer';
import HelpModeTrigger from './HelpModeTrigger';
import {
  useAdditionalResourceVideosQuery,
  useHelpModeLastRequiredWatchedTime,
  usePageVideosQuery,
  useReleaseVideosQuery,
} from './data/queries';
import { getId } from './data/selectors';
import { findUnwatchedRequiredVideo, maybeCreateCategory } from './helpers';
import HelpModeModal from './modal/HelpModeModal';

const HelpMode = ({ className: triggerClassName }) => {
  const pageId = useSelector(getId);
  // UI state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isPageSpecificDrawerOpen, setIsPageSpecificDrawerOpen] =
    useState(false);
  const [modalVideo, setModalVideo] = useState({
    isOpen: false,
    isClosable: true,
    video: null,
  });

  // Load all of the video types
  const releaseVideosQuery = useReleaseVideosQuery({
    refetchOnWindowFocus: false,
  });
  const pageVideosQuery = usePageVideosQuery(pageId, {
    enabled: !!pageId,
    refetchOnWindowFocus: false,
  });
  const additionalResourcesVideosQuery = useAdditionalResourceVideosQuery(
    pageId,
    { enabled: !!pageId, refetchOnWindowFocus: false },
  );

  // Consider help mode to be loading if any subset is loading
  const isLoading =
    releaseVideosQuery.isLoading ||
    pageVideosQuery.isLoading ||
    additionalResourcesVideosQuery.isLoading;

  // Categories of video to show in the drawer
  const videoCategories = [
    ...maybeCreateCategory("What's New", releaseVideosQuery.data?.video, {
      expandLinkText: releaseVideosQuery.data?.releaseNotes
        ? 'View Release Notes'
        : null,
      onExpandLinkClick: releaseVideosQuery.data?.releaseNotes,
    }),
    ...maybeCreateCategory('Help For This Page', pageVideosQuery.data?.video, {
      expandLinkText: 'View More',
      onExpandLinkClick: () => {
        setIsPageSpecificDrawerOpen(true);
      },
    }),
  ];

  // Required video to automatically open once loading is complete
  const [hasShownRequiredVideo, setHasShownRequiredVideo] = useState(false);
  const requiredVideo = isLoading
    ? null
    : findUnwatchedRequiredVideo(pageVideosQuery.data, releaseVideosQuery.data);

  const lastRequiredWatchedTime = useHelpModeLastRequiredWatchedTime(
    hasShownRequiredVideo,
  );

  useEffect(() => {
    if (
      !requiredVideo ||
      moment().diff(lastRequiredWatchedTime, 'hours') < 24
    ) {
      return;
    }

    /*
     * Record that a required video has already been popped.
     *
     * Without this check, the following sequence is possible:
     * - Page loads with 2 required videos
     * - The first required video appears
     * - The user selects "Back To Drawer" to browse videos on their own
     * - The second required video appears instead
     *
     * By keeping track of whether a required video ever came up and not showing
     * another required video for the page, the jarring UI behavior is mitigated.
     */
    setHasShownRequiredVideo(true);
    if (!isDrawerOpen && !modalVideo.isOpen && !hasShownRequiredVideo) {
      setModalVideo({ video: requiredVideo, isClosable: false, isOpen: true });
    }
  }, [
    hasShownRequiredVideo,
    isDrawerOpen,
    lastRequiredWatchedTime,
    modalVideo,
    requiredVideo,
  ]);

  return (
    <>
      <HelpModeTrigger
        className={triggerClassName}
        onClick={() => setIsDrawerOpen(!isDrawerOpen)}
      />
      <HelpModeDrawer
        pageId={pageId}
        videoCategories={videoCategories}
        isLoading={isLoading}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onVideoSelect={(
          video,
          { isTopLevelDrawerOpen, isPageSpecificDrawerOpen },
        ) => {
          setModalVideo({
            video,
            isOpen: true,
            isClosable: true,
            wasTopLevelDrawerOpen: isTopLevelDrawerOpen,
            wasPageSpecificDrawerOpen: isPageSpecificDrawerOpen,
          });
          setIsPageSpecificDrawerOpen(false);
          setIsDrawerOpen(false);
        }}
        isPageSpecificDrawerOpen={isPageSpecificDrawerOpen}
        onPageSpecificDrawerClose={() => {
          setIsPageSpecificDrawerOpen(false);
        }}
      />
      {modalVideo.video && (
        <HelpModeModal
          isClosable={modalVideo?.isClosable}
          isOpen={modalVideo.isOpen}
          onClose={() => {
            setModalVideo({ ...modalVideo, isOpen: false });
          }}
          onBack={() => {
            setIsDrawerOpen(modalVideo.wasTopLevelDrawerOpen);
            setIsPageSpecificDrawerOpen(modalVideo.wasPageSpecificDrawerOpen);
            setModalVideo({ ...modalVideo, isOpen: false });
          }}
          onGoToDrawer={() => {
            setIsDrawerOpen(true);
            setModalVideo({ ...modalVideo, isOpen: false });
          }}
          video={modalVideo?.video}
        />
      )}
    </>
  );
};

export default HelpMode;
